import { Router } from 'vue-router'

export interface NotificationData {
  notification_id?: string
  tipo?: string
  type?: string
  categoria?: string
  urgente?: string
  acao_tipo?: string
  acao_valor?: string
  acao_parametros?: string
  dados_extras?: string
  agendamento_id?: string
  ordem_servico_id?: string
  emergencia_id?: string
  event_type?: string
  status?: string
}

/**
 * Serviço de Deep Linking para navegação a partir de notificações push
 */
class DeeplinkService {
  private router: Router | null = null
  private readonly knownPayloadKeys = new Set([
    'notification_id',
    'tipo',
    'type',
    'categoria',
    'urgente',
    'acao_tipo',
    'acao_valor',
    'acao_parametros',
    'dados_extras',
    'agendamento_id',
    'ordem_servico_id',
    'emergencia_id',
    'event_type',
    'status',
    'title',
    'body',
    'message'
  ])

  init(router: Router) {
    this.router = router
  }

  /**
   * Processa os dados de uma notificação e navega para a página apropriada
   */
  async handleNotificationClick(data: NotificationData): Promise<boolean> {
    console.log('[Deeplink] Processando notificação:', data)

    if (!this.router) {
      console.warn('[Deeplink] Router não inicializado')
      return false
    }

    try {
      const payloadKeys = Object.keys(data || {})
      const hasKnownNotificationShape = payloadKeys.some((key) => this.knownPayloadKeys.has(key))
      if (!hasKnownNotificationShape) {
        console.log('[DeepLinkDebug][PushDeeplink] payload ignorado por não parecer notificação', {
          payloadKeys
        })
        return false
      }

      // Extrair dados extras se vier como string JSON
      let extras: Record<string, any> = {}
      if (data.dados_extras) {
        try {
          extras = typeof data.dados_extras === 'string'
            ? JSON.parse(data.dados_extras)
            : data.dados_extras
        } catch (e) {
          console.warn('[Deeplink] Erro ao parsear dados_extras:', e)
        }
      }

      // Extrair parâmetros de ação se vier como string JSON
      let params: Record<string, any> = {}
      if (data.acao_parametros) {
        try {
          params = typeof data.acao_parametros === 'string'
            ? JSON.parse(data.acao_parametros)
            : data.acao_parametros
        } catch (e) {
          console.warn('[Deeplink] Erro ao parsear acao_parametros:', e)
        }
      }

      // Combinar todos os dados
      const allData = { ...data, ...extras, ...params }

      // Determinar a rota com base na categoria e ação
      const route = this.buildRouteFromNotification(allData)

      if (route) {
        console.log('[Deeplink] Navegando para:', route)
        await this.router.push(route)
        return true
      }

      console.warn('[Deeplink] Nenhuma rota encontrada para os dados:', allData)
      return false
    } catch (error) {
      console.error('[Deeplink] Erro ao processar notificação:', error)
      return false
    }
  }

  /**
   * Constrói a rota baseada nos dados da notificação
   */
  private buildRouteFromNotification(data: Record<string, any>): { name: string; params?: Record<string, any>; query?: Record<string, any> } | null {
    const categoria = data.categoria
    const acaoTipo = data.acao_tipo
    const acaoValor = data.acao_valor
    const eventType = data.event_type || data.type

    // Se tiver ação explícita do tipo deep_link, usar o valor diretamente
    if (acaoTipo === 'deep_link' && acaoValor) {
      // Verificar se é um path completo ou um nome de rota
      if (acaoValor.startsWith('/')) {
        // Path completo - navegar diretamente
        return { name: 'UsuarioAgendamentoShow', params: { id: data.agendamento_id } }
      }
    }

    // Categoria: agendamento
    if (categoria === 'agendamento' || data.agendamento_id) {
      const agendamentoId = data.agendamento_id || data.dados_extras?.agendamento_id
      if (agendamentoId) {
        return { name: 'UsuarioAgendamentoShow', params: { id: agendamentoId } }
      }
    }

    // Categoria: diagnostico
    if (categoria === 'diagnostico' || data.diagnostico_id) {
      const diagnosticoId = data.diagnostico_id || data.dados_extras?.diagnostico_id
      if (diagnosticoId) {
        return { name: 'UsuarioDiagnosticoShow', params: { id: diagnosticoId } }
      }
    }

    // Categoria: emergencia
    if (categoria === 'emergencia' || data.emergencia_id || eventType === 'sos.accepted' || eventType === 'sos.completed') {
      const emergenciaId = data.emergencia_id || data.dados_extras?.emergencia_id
      if (emergenciaId) {
        if (data.status === 'concluida' || eventType === 'sos.completed') {
          return { name: 'UsuarioEmergenciaFinalizado', params: { id: emergenciaId } }
        }

        return {
          name: 'UsuarioEmergencia',
          query: {
            aceito: '1',
            emergencia: String(emergenciaId)
          }
        }
      }

      return { name: 'UsuarioEmergencia' }
    }

    // Categoria: orcamento
    if (categoria === 'orcamento' || data.orcamento_id) {
      const orcamentoId = data.orcamento_id || data.dados_extras?.orcamento_id
      if (orcamentoId) {
        return { name: 'UsuarioOrcamentoShow', params: { id: orcamentoId } }
      }
    }

    // Categoria: pagamento
    if (categoria === 'pagamento' || data.pagamento_id) {
      const pagamentoId = data.pagamento_id || data.dados_extras?.pagamento_id
      if (pagamentoId && data.agendamento_id) {
        return { name: 'UsuarioAgendamentoPagamento', params: { id: data.agendamento_id } }
      }
    }

    // Fallback: sem rota conhecida para este payload.
    return null
  }

  /**
   * Processa notificação recebida enquanto o app está aberto
   */
  async handleNotificationReceived(notification: any): Promise<void> {
    console.log('[Deeplink] Notificação recebida em foreground:', notification)
    const data = notification?.data || notification?.notification?.data || {}
    const eventType = data.event_type || data.type

    if (eventType === 'sos.accepted' || eventType === 'sos.completed' || data.emergencia_id) {
      window.dispatchEvent(new CustomEvent('sosPushUpdate', { detail: data }))
      await this.handleNotificationClick(data)
    }
  }
}

// Singleton
export const deeplinkService = new DeeplinkService()
